import { useEffect, useState } from 'react';
import _ from 'lodash';
import {
  Arrow,
  Circle,
  Ellipse,
  Group,
  Image as KonvaImage,
  Path,
  Rect,
  RegularPolygon,
  Ring,
  Shape as KonvaShape,
  Star,
} from 'react-konva';
import {
  casesInGrid,
  gridHeight,
  gridWidth,
} from './../../constants/gridConfig';
import {
  defaultItemValues,
  ItemTypes,
  SHAPE_KIND_LINE,
} from '../../constants/constants';
import {
  collectLinks,
  computeLinkGeometry,
  markerSizes,
  mutateRemoveLinksTo,
  traceLinkPath,
  traceMarker,
} from './shapeLinks';

export const NO_HEIGHT_TYPES = [
  'img',
  'text',
  'title1',
  'title2',
  'title3',
  'title4',
  'summary',
  'table',
  ItemTypes.CHECKBOXS_VARIABLE,
  ItemTypes.QUESTION,
  ItemTypes.SELECTOR_VARIABLE,
  ItemTypes.TEXT_VARIABLE,
  ItemTypes.IMG_VARIABLE,
  ItemTypes.TABLE_VARIABLE,
  ItemTypes.ORG_CHART,
  // Volet et Carte info : la hauteur suit leurs textes, comme une box texte.
  ItemTypes.ACCORDION_PANEL,
  ItemTypes.INFO_CARD,
];
export const LOCK_PROPORTIONS_TYPES = ['img', ItemTypes.IMG_VARIABLE, ItemTypes.ORG_CHART];

// Couleur de la zone d'affichage (box complète) dans l'éditeur plein écran :
// contour en pointillé et titre de sa section dans le panneau.
export const ZONE_COLOR = '#52c41a';
// Couleur de la sous-forme sélectionnée : cadre en pointillé et titre de la
// section « Formes » dans le panneau.
export const SUB_SHAPE_COLOR = '#1677ff';

export const NO_ROTATION_SHAPES = ['round', 'ring'];

export const starInnerRadius = (boxWidth, boxBorderSize) => {
  const rStar = starRadius(boxWidth, boxBorderSize);
  return (
    (rStar * Math.sin((18 * Math.PI) / 180)) / Math.cos((36 * Math.PI) / 180)
  );
};

export const shapeRatios = (shapeId) => {
  const ratios = {
    star: Math.cos((18 * Math.PI) / 180),
    triangle: Math.cos((30 * Math.PI) / 180),
    hexagone: 1 / Math.cos((30 * Math.PI) / 180),
  };
  return ratios[shapeId] ?? 1;
};

export const LOCK_PROPORTIONS_SHAPES = [
  'square',
  'round',
  'star',
  'ring',
  'triangle',
  'hexagone',
  // Un SVG importé (forme ou image) n'est jamais atteint via
  // mutateSetSubShapeKind (pas une option du sélecteur de type, cf.
  // ShapeComponent) : sa présence ici ne joue que sur le Transformer
  // (keepRatio), pour ne pas déformer l'icône.
  'svg',
  'svg-image',
];

// Formes dont l'utilisateur peut figer les proportions (`box.lockRatio`) :
// celles qui se redimensionnent aujourd'hui librement dans les deux sens.
// Les formes de LOCK_PROPORTIONS_SHAPES sont déjà figées par nature, et la
// flèche comme la ligne n'ont qu'une poignée de largeur — leur hauteur ne se
// règle pas au glissement, il n'y a rien à y figer.
export const RATIO_LOCKABLE_SHAPES = ['rectangle', 'ellipse'];

export const canLockShapeRatio = (box) =>
  RATIO_LOCKABLE_SHAPES.includes(box?.content?.shape) && !isCompoundShape(box);

// Le drapeau reste sur la box quand on change son type de forme : on le relit
// à chaque fois plutôt que de le nettoyer, sinon un rectangle figé puis passé
// en flèche garderait des proportions verrouillées sans le montrer nulle part.
export const isShapeRatioLocked = (box) => !!box?.lockRatio && canLockShapeRatio(box);

const CORNER_ANCHORS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
const EDGE_ANCHORS = ['middle-left', 'middle-right', 'top-center', 'bottom-center'];

// Ancres actives du Transformer d'une sous-forme (cf. Shape.jsx,
// ShapeFullscreenEditor.jsx) : les ancres de bord (milieu d'un côté) ne
// redimensionnent qu'une seule dimension, quoi qu'il arrive — `keepRatio` ne
// contraint que les coins (limitation Konva). Les exclure pour une forme à
// proportions verrouillées (cf. LOCK_PROPORTIONS_SHAPES) : sans ça, glisser un
// bord seul suffit à la déformer malgré `keepRatio` (ex. un carré qui devient
// un rectangle, mais dont le rendu — cf. getShapeElement, qui ignore
// `height` pour ce type — semble « revenir » carré une fois relâché).
export const subShapeTransformerAnchors = (shapeKind) =>
  LOCK_PROPORTIONS_SHAPES.includes(shapeKind) ? CORNER_ANCHORS : [...CORNER_ANCHORS, ...EDGE_ANCHORS];

// Formes à angles pouvant recevoir un rayon d'arrondi (`cornerRadius` Konva).
// `cornerRadius` sur `RegularPolygon` (triangle, hexagone) n'existe qu'à partir
// de Konva 10 — nécessite l'upgrade depuis la 8.4.3.
export const RADIUS_CAPABLE_SHAPES = ['square', 'rectangle', 'triangle', 'hexagone'];

// Nombre d'angles de chaque forme radius-capable : détermine la taille du
// tableau `box.cornerRadii` (un rayon par angle) et le nombre de champs
// affichés dans le panneau « Par angle ».
export const CORNER_COUNT_BY_SHAPE = {
  square: 4,
  rectangle: 4,
  triangle: 3,
  hexagone: 6,
};

export const goldRatio = (1 + Math.sqrt(5)) / 2;

export const shapeRadius = (shapeId, boxWidth) => {
  return boxWidth / (2 * shapeRatios(shapeId));
};

// Rayon Konva réel du polygone (distance centre-sommet) pour un triangle/
// hexagone, tel qu'utilisé par getShapeComponent — nécessaire pour borner le
// rayon d'angle : passé la moitié de ce rayon, Konva arrondit déjà en cercle
// parfait (cf. Util.drawRoundedPolygonPath), la suite du curseur ne changerait
// plus rien à l'affichage. Prend des primitives (pas une box) : appelable
// aussi bien pour une forme unique que pour une sous-forme d'une forme composée.
export const getPolygonRadius = (shapeKind, width, height, borderSize) => {
  switch (shapeKind) {
    case 'triangle':
      return shapeRadius('triangle', width);
    case 'hexagone':
      return (height - borderSize) / 2;
    default:
      return null;
  }
};

export const starRadius = (boxWidth, boxBorderSize) => {
  const borderAddedWidth = boxBorderSize / Math.tan((18 * Math.PI) / 180);
  const innerBoxHalfWidth = (boxWidth - borderAddedWidth) / 2; // - borderAddedWidth
  return innerBoxHalfWidth / Math.cos((18 * Math.PI) / 180);
};

export const outerBorderRadiusExtension = (boxBorderSize) => {
  return boxBorderSize / Math.sin((18 * Math.PI) / 180) / 2;
};

export const innerBorderRadiusExtension = (borderWidth) => {
  return borderWidth / Math.cos((54 * Math.PI) / 180);
};

export const starYDelta = (boxBorderSize) => {
  return outerBorderRadiusExtension(boxBorderSize);
};

/**
 * Nature courante d'une box « Forme » telle que le sélecteur la présente : une
 * forme (carré, rond…) ou une ligne.
 */
export const getShapeKind = (box) =>
  box?.type === ItemTypes.LINE ? SHAPE_KIND_LINE : box?.content?.shape;

/**
 * Change la nature d'une box « Forme » : une autre forme, ou une ligne.
 *
 * Ligne et forme restent deux types de box — la ligne se rend en CSS, les formes
 * dans un canvas Konva, et on ne change rien au rendu des documents existants.
 * Côté utilisateur il n'y a plus qu'un élément « Forme » dont la ligne est une
 * variante : c'est ici que la box passe d'un type à l'autre.
 */
export const mutateSetShapeKind = (box, shapeId, { maxWidth } = {}) => {
  if (!box) return;
  const lineDefaults = defaultItemValues.line;
  const shapeDefaults = defaultItemValues.shape;

  if (shapeId === SHAPE_KIND_LINE) {
    if (box.type === ItemTypes.LINE) return;
    // Forme → ligne : une ligne porte ses réglages à plat (cf. Line/index.jsx)
    // et la couleur de la forme devient celle du trait. La hauteur devient
    // l'épaisseur du trait, et la largeur passe au moins à celle d'une ligne
    // neuve : garder celle d'une forme (huit fois plus étroite) donnait un
    // trait minuscule là où l'ancien élément « Ligne » traversait la page.
    // `maxWidth` (largeur utile de la cellule, hors mode dessin) borne le
    // résultat : l'éditeur rogne l'affichage d'une box trop large pour sa
    // colonne, mais le PDF rend la largeur stockée telle quelle et la ligne
    // dépasserait.
    box.type = ItemTypes.LINE;
    box.width = Math.min(
      Math.max(box.width ?? 0, lineDefaults.width),
      maxWidth ?? Infinity
    );
    box.color = box.content?.color ?? lineDefaults.content.color;
    box.style = box.style ?? lineDefaults.content.style;
    box.lineHeight = box.lineHeight ?? lineDefaults.content.height;
    box.side = box.side ?? lineDefaults.content.side;
    box.height = box.lineHeight;
    delete box.content;
    return;
  }

  if (box.type === ItemTypes.LINE) {
    // Ligne → forme : la couleur du trait devient la couleur de fond. La box
    // reprend les dimensions d'une forme neuve, sinon une forme aussi large
    // qu'une ligne (24 colonnes de grille) remplirait la page — sauf la flèche,
    // qui est elle-même un trait et garde donc la largeur de la ligne.
    box.type = ItemTypes.SHAPE;
    box.content = {
      color: box.color ?? shapeDefaults.content.color,
      shape: shapeId,
    };
    box.borders = box.borders ?? {
      color: { ...shapeDefaults.borders.color },
      size: shapeDefaults.borders.size,
    };
    box.arrows = box.arrows ?? { ...shapeDefaults.arrows };
    box.stroke = box.stroke ?? shapeDefaults.stroke;
    box.rotation = box.rotation ?? shapeDefaults.rotation;
    box.radius = box.radius ?? shapeDefaults.radius;
    box.shadow = box.shadow ?? { ...shapeDefaults.shadow, color: { ...shapeDefaults.shadow.color } };
    if (shapeId !== 'arrow') {
      box.width = shapeDefaults.width;
      box.height = shapeDefaults.height;
    }
    delete box.color;
    delete box.style;
    delete box.lineHeight;
    delete box.side;
  }

  // Hauteur imposée par la nature de la forme : la flèche se dimensionne sur ses
  // pointes, les formes à proportions verrouillées sur leur largeur.
  if (shapeId === 'arrow') {
    box.height = box.stroke + box.arrows.stroke + 20;
  } else if (LOCK_PROPORTIONS_SHAPES.includes(shapeId)) {
    box.height = box.width * shapeRatios(shapeId);
  }
  // Un rayon par angle ne veut plus dire la même chose une fois le nombre de
  // côtés changé (4 coins de rectangle vs 3 d'un triangle) : on l'efface, le
  // rayon uniforme (box.radius) reste un réglage valable sur toute forme.
  if (box.content.shape !== shapeId) delete box.cornerRadii;
  box.content.shape = shapeId;
};

// Props Konva d'ombre portée, communes à toutes les formes : un seul point de
// vérité pour blur/décalage/opacité/couleur, plutôt que de les répéter dans
// chaque `case` de getShapeElement. Prend l'objet `shadow` directement (pas
// une box) : partagé entre une forme unique (`box.shadow`) et une sous-forme
// d'une forme composée (`subShape.shadow`).
const shadowProps = (shadow) => {
  if (!shadow?.enabled) return {};
  return {
    shadowColor: shadow.color?.style,
    shadowBlur: shadow.blur,
    shadowOffsetX: shadow.offsetX,
    shadowOffsetY: shadow.offsetY,
    shadowOpacity: shadow.opacity,
  };
};

// Marge (px) nécessaire pour qu'une ombre portée ait la place de se dessiner :
// le Stage Konva doit être agrandi d'autant sur chaque côté (cf. Shape.jsx),
// sans quoi elle serait purement rognée au bord du canvas. Plafonnée à une
// valeur fixe plutôt qu'à une fraction de la box : rien n'oblige plus à
// rétrécir la forme, la seule limite raisonnable est d'éviter qu'un flou/
// décalage extrême ne fasse déborder le canvas loin dans le contenu voisin.
const MAX_SHADOW_MARGIN = 60;
export const getShadowCanvasMargin = (shadow) => {
  if (!shadow?.enabled) return 0;
  const raw = shadow.blur + Math.max(Math.abs(shadow.offsetX), Math.abs(shadow.offsetY));
  return Math.min(raw, MAX_SHADOW_MARGIN);
};

// Vignette de sauvegarde en bibliothèque (cf. Shape.jsx/ShapeFullscreenEditor)
// : capture du Stage réellement affiché, pas un rendu Konva séparé. Masque le
// temps de la capture le Transformer ET le repère de sélection d'une
// sous-forme (`subShapeSelectionIndicator`, cf. getShapeComponent) — sans ça,
// leurs pointillés se retrouvent gravés dans la vignette enregistrée. Toggle
// direct sur les nœuds Konva (pas un state React) : synchrone, donc invisible
// à l'écran (le navigateur ne peint qu'après ce bloc, pas entre le masquage
// et la restauration).
// `cropRect` ({x, y, width, height}, en pixels absolus du Stage) restreint la
// capture à la zone de contenu utile — indispensable dès que le Stage est
// plus grand que son contenu (cf. ShapeFullscreenEditor.jsx, dont le Stage
// remplit tout le conteneur pour laisser de la place aux poignées de la
// zone pendant un drag) : sans elle, la vignette inclurait toute la marge
// transparente autour de la forme.
export const captureStageThumbnail = (stage, transformer, cropRect) => {
  if (!stage) return undefined;
  // `.zoneOverlay` : le cadre en pointillé de la zone d'affichage et son
  // Transformer (cf. ShapeFullscreenEditor.jsx) — une UI d'édition, jamais
  // censée finir dans la vignette enregistrée. `.linkEditorUi` : poignées et
  // tracé provisoire de création de lien, même chose.
  const indicators = stage.find('.subShapeSelectionIndicator, .zoneOverlay, .linkEditorUi');
  const wasTransformerVisible = transformer?.visible();
  transformer?.visible(false);
  indicators.forEach((node) => node.visible(false));
  stage.batchDraw();
  const dataUrl = stage.toDataURL({ mimeType: 'image/png', ...cropRect });
  if (transformer) transformer.visible(wasTransformerVisible);
  indicators.forEach((node) => node.visible(true));
  stage.batchDraw();
  return dataUrl;
};

// Convertit un élément de forme SVG basique en tracé `d` équivalent — pas de
// <path> à extraire directement, mais une formule géométrique connue. Chaque
// famille d'icônes a ses habitudes (Font Awesome/Material Symbols : <path> ;
// des pictos plus simples : <rect>/<circle>/<polygon>...), autant couvrir les
// deux plutôt que de rejeter la moitié des fichiers déposés.
const shapeElementToPathData = (el) => {
  const num = (name, fallback = 0) => parseFloat(el.getAttribute(name)) || fallback;
  switch (el.tagName.toLowerCase()) {
    case 'rect': {
      const x = num('x');
      const y = num('y');
      const w = num('width');
      const h = num('height');
      if (!w || !h) return null;
      const rx = num('rx', num('ry'));
      const ry = num('ry', rx);
      if (!rx && !ry) return `M ${x},${y} L ${x + w},${y} L ${x + w},${y + h} L ${x},${y + h} Z`;
      return `M ${x + rx},${y} L ${x + w - rx},${y} A ${rx},${ry} 0 0 1 ${x + w},${y + ry}`
        + ` L ${x + w},${y + h - ry} A ${rx},${ry} 0 0 1 ${x + w - rx},${y + h}`
        + ` L ${x + rx},${y + h} A ${rx},${ry} 0 0 1 ${x},${y + h - ry}`
        + ` L ${x},${y + ry} A ${rx},${ry} 0 0 1 ${x + rx},${y} Z`;
    }
    case 'circle': {
      const cx = num('cx');
      const cy = num('cy');
      const r = num('r');
      if (!r) return null;
      return `M ${cx - r},${cy} A ${r},${r} 0 1 0 ${cx + r},${cy} A ${r},${r} 0 1 0 ${cx - r},${cy} Z`;
    }
    case 'ellipse': {
      const cx = num('cx');
      const cy = num('cy');
      const rx = num('rx');
      const ry = num('ry');
      if (!rx || !ry) return null;
      return `M ${cx - rx},${cy} A ${rx},${ry} 0 1 0 ${cx + rx},${cy} A ${rx},${ry} 0 1 0 ${cx - rx},${cy} Z`;
    }
    case 'polygon':
    case 'polyline': {
      const points = (el.getAttribute('points') || '').trim().split(/[\s,]+/).map(Number);
      if (points.length < 4) return null;
      let d = `M ${points[0]},${points[1]}`;
      for (let i = 2; i < points.length - 1; i += 2) d += ` L ${points[i]},${points[i + 1]}`;
      return el.tagName.toLowerCase() === 'polygon' ? `${d} Z` : d;
    }
    case 'line':
      return `M ${num('x1')},${num('y1')} L ${num('x2')},${num('y2')}`;
    default:
      return null;
  }
};

// Extrait d'un fichier .svg déposé par l'utilisateur ce qu'il faut pour le
// rendre comme sous-forme (cf. getShapeElement, case 'svg') : le(s) tracé(s)
// (le `d` de chaque <path>, ou l'équivalent calculé pour un <rect>/<circle>/
// <ellipse>/<polygon>/<polyline>/<line> — cf. shapeElementToPathData — le tout
// concaténé : la syntaxe SVG path autorise plusieurs sous-tracés dans un seul
// `d`) et les dimensions du viewBox, qui servent à remettre le tracé à
// l'échelle de la sous-forme. Rendu en une seule couleur, celle de la forme
// (pas les couleurs d'origine du fichier) : ne restitue donc fidèlement
// qu'une icône mono-couleur — un fichier multi-couleurs se fond en une seule
// teinte plutôt que d'être rejeté. Une transformation (`transform` sur un
// élément ou un <g> englobant) n'est pas prise en compte.
const svgViewBoxFromElement = (svg) => {
  const viewBoxAttr = svg.getAttribute('viewBox');
  if (viewBoxAttr) {
    const parts = viewBoxAttr.trim().split(/[\s,]+/).map(Number);
    return { width: parts[2], height: parts[3] };
  }
  return {
    width: parseFloat(svg.getAttribute('width')) || 24,
    height: parseFloat(svg.getAttribute('height')) || 24,
  };
};

export const parseSvgFile = (text) => {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;
  const svg = doc.querySelector('svg');
  if (!svg) return null;

  const svgPath = Array.from(svg.querySelectorAll('path, rect, circle, ellipse, polygon, polyline, line'))
    .map((el) => (el.tagName.toLowerCase() === 'path' ? el.getAttribute('d') : shapeElementToPathData(el)))
    .filter(Boolean)
    .join(' ');
  if (!svgPath) return null;

  const svgViewBox = svgViewBoxFromElement(svg);
  if (!svgViewBox.width || !svgViewBox.height) return null;

  return { svgPath, svgViewBox };
};

// Pour l'import "Image" (cf. ShapeComponent#handleSvgImportChoice) : le
// fichier garde ses couleurs et n'est plus modifiable comme une forme, son
// tracé n'a donc pas besoin d'être extrait — seul le viewBox sert, à
// dimensionner la sous-forme. Le fichier lui-même est encodé à part en data
// URI (cf. mutateAddImportedSubShape) pour rester portable d'un export à
// l'autre (PDF, un autre visiteur du document) plutôt qu'une URL locale au
// navigateur.
export const parseSvgViewBox = (text) => {
  const doc = new DOMParser().parseFromString(text, 'image/svg+xml');
  if (doc.querySelector('parsererror')) return null;
  const svg = doc.querySelector('svg');
  if (!svg) return null;

  const svgViewBox = svgViewBoxFromElement(svg);
  if (!svgViewBox.width || !svgViewBox.height) return null;

  return svgViewBox;
};

// Échelle à appliquer au Group interne d'un SVG importé (forme ou image) pour
// ramener son viewBox d'origine à la taille de la sous-forme — partagé par
// les deux rendus ('svg' et 'svg-image').
const svgFitScale = (descriptor) => {
  const viewBox = descriptor.svgViewBox?.width && descriptor.svgViewBox?.height
    ? descriptor.svgViewBox
    : { width: descriptor.width, height: descriptor.height };
  return { scaleX: descriptor.width / viewBox.width, scaleY: descriptor.height / viewBox.height, viewBox };
};

// Rendu d'un SVG importé "Image" (cf. parseSvgViewBox) : contrairement à
// 'svg' (un <Path> recolorable), le fichier garde ses couleurs d'origine —
// chargé tel quel dans une image Konva. Un composant à part (pas juste un
// `case` de getShapeElement) car le chargement est asynchrone : un <img>
// natif doit décoder le SVG avant que Konva puisse le dessiner.
const SubShapeSvgImage = ({ descriptor }) => {
  const [image, setImage] = useState(null);
  useEffect(() => {
    if (!descriptor.svgDataUrl) {
      setImage(null);
      return undefined;
    }
    let cancelled = false;
    const img = new window.Image();
    img.onload = () => {
      if (!cancelled) setImage(img);
    };
    img.src = descriptor.svgDataUrl;
    return () => {
      cancelled = true;
    };
  }, [descriptor.svgDataUrl]);

  const { scaleX, scaleY, viewBox } = svgFitScale(descriptor);
  return (
    <Group key={descriptor.id} id={descriptor.id} scaleX={scaleX} scaleY={scaleY}>
      {image && (
        <KonvaImage
          {...shadowProps(descriptor.shadow)}
          image={image}
          width={viewBox.width}
          height={viewBox.height}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          strokeScaleEnabled={false}
        />
      )}
    </Group>
  );
};

// Rend la géométrie Konva d'une forme à partir d'un descripteur à plat — pas
// une box directement : partagé entre une box « Forme » classique (un seul
// descripteur dérivé de la box, cf. legacyDescriptor) et chaque sous-forme
// d'une forme composée (le descripteur est alors la sous-forme elle-même).
const getShapeElement = (descriptor) => {
  switch (descriptor.shape) {
    case 'square':
      return (
        <Rect
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.borders.size / 2}
          y={descriptor.borders.size / 2}
          width={descriptor.width - descriptor.borders.size}
          height={descriptor.width - descriptor.borders.size}
          cornerRadius={descriptor.cornerRadii ?? descriptor.radius ?? 0}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
    case 'round':
      return (
        <Circle
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2 - 0.2}
          y={descriptor.width / 2 - 0.2}
          radius={descriptor.width / 2 - descriptor.borders.size / 2 - 0.2}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
    case 'rectangle':
      return (
        <Rect
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.borders.size / 2}
          y={descriptor.borders.size / 2}
          width={descriptor.width - descriptor.borders.size}
          height={descriptor.height - descriptor.borders.size}
          cornerRadius={descriptor.cornerRadii ?? descriptor.radius ?? 0}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
    case 'ellipse':
      return (
        <Ellipse
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2 - 0.2}
          y={descriptor.height / 2 - 0.2}
          radiusX={descriptor.width / 2 - descriptor.borders.size / 2 - 0.2}
          radiusY={descriptor.height / 2 - descriptor.borders.size / 2 - 0.2}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
    case 'star':
      return (
        <Star
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2}
          y={
            starRadius(descriptor.width, descriptor.borders.size) +
            outerBorderRadiusExtension(descriptor.borders.size)
          }
          numPoints={5}
          innerRadius={starInnerRadius(descriptor.width, descriptor.borders.size)}
          outerRadius={starRadius(descriptor.width, descriptor.borders.size)}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fillAfterStrokeEnabled={true}
          fill={descriptor.color}
        />
      );
    case 'triangle':
      return (
        <RegularPolygon
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2}
          y={shapeRadius('triangle', descriptor.width)}
          sides={3}
          radius={shapeRadius('triangle', descriptor.width)}
          cornerRadius={descriptor.cornerRadii ?? descriptor.radius ?? 0}
          stroke={descriptor.borders.color.style}
          fill={descriptor.color}
        />
      );
    case 'hexagone':
      const h = descriptor.height - descriptor.borders.size;
      return (
        <RegularPolygon
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2}
          y={descriptor.height / 2}
          sides={6}
          radius={h / 2}
          cornerRadius={descriptor.cornerRadii ?? descriptor.radius ?? 0}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
    case 'ring':
      return (
        <Ring
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.width / 2}
          y={descriptor.height / 2}
          innerRadius={descriptor.width / 2 - descriptor.borders.size}
          outerRadius={descriptor.width * 0.4}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
        />
      );
    case 'arrow':
      const pointerLength =
        descriptor.arrows.strokeLength ?? shapeRadius('triangle', descriptor.arrows.stroke);
      return (
        <Arrow
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          pointerAtBeginning={descriptor.arrows.left}
          pointerAtEnding={descriptor.arrows.right}
          points={[
            descriptor.arrows.left ? pointerLength + 1.5 : 0,
            descriptor.height / 2,
            descriptor.arrows.right ? descriptor.width - pointerLength - 2 : descriptor.width,
            descriptor.height / 2,
          ]}
          width={descriptor.width}
          height={descriptor.height}
          pointerLength={
            descriptor.arrows.strokeLength ??
            shapeRadius('triangle', descriptor.arrows.stroke)
          }
          pointerWidth={descriptor.arrows.stroke}
          stroke={descriptor.color}
          strokeWidth={descriptor.stroke}
          fill={descriptor.color}
        />
      );
    case 'svg': {
      // Le tracé garde les coordonnées de son viewBox d'origine (souvent
      // 24×24) : un Group intermédiaire à l'échelle le ramène à la taille de
      // la sous-forme, sans retoucher `data`. strokeScaleEnabled=false pour
      // que la taille de bordure reste en pixels réels malgré cette échelle,
      // comme les autres formes (descriptor.borders.size).
      const { scaleX, scaleY } = svgFitScale(descriptor);
      return (
        <Group key={descriptor.id} id={descriptor.id} scaleX={scaleX} scaleY={scaleY}>
          <Path
            {...shadowProps(descriptor.shadow)}
            data={descriptor.svgPath}
            stroke={descriptor.borders.color.style}
            strokeWidth={descriptor.borders.size}
            strokeScaleEnabled={false}
            fill={descriptor.color}
            fillAfterStrokeEnabled={true}
          />
        </Group>
      );
    }
    case 'svg-image':
      return <SubShapeSvgImage key={descriptor.id} descriptor={descriptor} />;
    // Bloc texte libre (cf. shapeText.js) : le texte est rendu par-dessus le
    // canvas ; ici, seulement son fond éventuel — transparent sinon, mais
    // toujours rempli pour rester attrapable sur toute sa surface.
    case 'text':
      return (
        <Rect
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          width={descriptor.width}
          height={descriptor.height}
          fill={descriptor.color ?? 'transparent'}
          stroke={descriptor.borders?.size ? descriptor.borders.color.style : undefined}
          strokeWidth={descriptor.borders?.size ?? 0}
        />
      );
    default:
      return (
        <Rect
          key={descriptor.id}
          id={descriptor.id}
          {...shadowProps(descriptor.shadow)}
          x={descriptor.borders.size / 2}
          y={descriptor.borders.size / 2}
          width={descriptor.width - descriptor.borders.size}
          height={descriptor.width - descriptor.borders.size}
          stroke={descriptor.borders.color.style}
          strokeWidth={descriptor.borders.size}
          fill={descriptor.color}
          fillAfterStrokeEnabled={true}
        />
      );
  }
};

// Descripteur à plat d'une box « Forme » classique — dérivé de la box pour
// nourrir getShapeElement, qui ne connaît que des descripteurs.
const legacyDescriptor = (box) => ({
  id: box.id,
  shape: box.content.shape,
  color: box.content.color,
  width: box.width,
  height: box.height,
  borders: box.borders,
  radius: box.radius,
  cornerRadii: box.cornerRadii,
  shadow: box.shadow,
  arrows: box.arrows,
  stroke: box.stroke,
});

// Descripteur à plat d'une sous-forme (déjà stockée à plat dans
// box.content.shapes) — x/y/rotation en plus, consommés par getShapeComponent
// pour positionner le <Group> Konva de la sous-forme, pas par getShapeElement.
const subShapeDescriptor = (sub) => ({
  id: sub.id,
  shape: sub.shape,
  color: sub.color,
  width: sub.width,
  height: sub.height,
  borders: sub.borders,
  radius: sub.radius,
  cornerRadii: sub.cornerRadii,
  shadow: sub.shadow,
  arrows: sub.arrows,
  stroke: sub.stroke,
  svgPath: sub.svgPath,
  svgViewBox: sub.svgViewBox,
  svgDataUrl: sub.svgDataUrl,
});

// Une box « Forme » est composée dès qu'elle porte plusieurs sous-formes dans
// content.shapes — sinon elle garde le format historique (content.shape à
// plat sur la box). Les deux formats coexistent : aucune box existante n'est
// migrée, elles restent rendues par le chemin `legacyDescriptor`.
export const isCompoundShape = (box) =>
  Array.isArray(box?.content?.shapes) && box.content.shapes.length > 0;

// Effondre une forme composée en une forme simple d'un type donné — l'inverse
// de mutateStartCompoundShape : plus aucun réglage par sous-forme n'est
// accessible hors de l'éditeur plein écran (cf. Shape.jsx), donc changer le
// sélecteur Type d'une forme composée n'a plus de sens comme « changer le
// type de la sous-forme active » — ça redevient une forme unique, comme si
// elle n'avait jamais été composée. Mêmes réglages par défaut que le passage
// ligne → forme (cf. mutateSetShapeKind), qui recalcule ensuite la hauteur et
// pose `content.shape` — sauf la couleur, en principe celle configurée pour
// le document (`configuration.shapeColor.style`, cf. Shape.jsx#handleAddShape)
// plutôt que le vert/sarcelle de secours de defaultItemValues, qui ne sert
// que si aucune n'est fournie.
export const mutateCollapseCompoundToShapeKind = (box, shapeId, defaultColor) => {
  if (!isCompoundShape(box)) return;
  const shapeDefaults = defaultItemValues.shape;
  box.content = { color: defaultColor ?? shapeDefaults.content.color };
  box.borders = { color: { ...shapeDefaults.borders.color }, size: shapeDefaults.borders.size };
  box.arrows = { ...shapeDefaults.arrows };
  box.stroke = shapeDefaults.stroke;
  box.rotation = shapeDefaults.rotation;
  box.radius = shapeDefaults.radius;
  box.shadow = { ...shapeDefaults.shadow, color: { ...shapeDefaults.shadow.color } };
  delete box.cornerRadii;
  mutateSetShapeKind(box, shapeId);
};

// Redimensionne toutes les sous-formes proportionnellement quand la box
// elle-même est redimensionnée par sa poignée générique (cf. Resizer.jsx) —
// sans ça, chaque sous-forme garde sa position/taille en pixels absolus :
// rétrécir la box laisse alors les sous-formes déborder de ses nouveaux
// bords (et disparaître si un ancêtre en mode dessin les rogne), l'agrandir
// ne change visuellement rien. Appelée seulement au relâché (onResizeStop) :
// pendant le glissement, c'est getCompoundDesignScale qui donne un aperçu en
// direct sans toucher aux données stockées.
export const mutateRescaleSubShapes = (box, oldWidth, oldHeight) => {
  if (!isCompoundShape(box) || !oldWidth || !oldHeight) return;
  const scaleX = box.width / oldWidth;
  const scaleY = box.height / oldHeight;
  if (scaleX === 1 && scaleY === 1) return;
  box.content.shapes.forEach((sub) => {
    sub.x = (sub.x ?? 0) * scaleX;
    sub.y = (sub.y ?? 0) * scaleY;
    sub.width = sub.width * scaleX;
    sub.height = sub.height * scaleY;
  });
};

// Fixe la largeur ou la hauteur d'une sous-forme (saisie dans le panneau). Une
// forme à proportions verrouillées (cf. LOCK_PROPORTIONS_SHAPES) n'est dessinée
// qu'à partir de sa largeur (cf. getShapeElement) : changer seulement la hauteur
// ne ferait bouger que le cadre de sélection, pas la forme. L'autre dimension
// suit donc, au ratio propre à la forme (cf. shapeRatios) ou, pour un SVG, à
// celui qu'il a déjà. Les autres formes gardent des dimensions indépendantes.
export const mutateSetSubShapeSize = (sub, field, value) => {
  if (!LOCK_PROPORTIONS_SHAPES.includes(sub.shape)) {
    sub[field] = value;
    return;
  }
  const isSvg = sub.shape === 'svg' || sub.shape === 'svg-image';
  // hauteur / largeur, lu avant d'écraser la dimension saisie
  const ratio = isSvg ? (sub.width && sub.height ? sub.height / sub.width : 1) : shapeRatios(sub.shape);
  if (field === 'width') {
    sub.width = value;
    sub.height = value * ratio;
  } else {
    sub.height = value;
    sub.width = value / ratio;
  }
};

// Échelle d'aperçu en direct pendant un glissement de la poignée générique de
// la box (cf. Resizer.jsx) : ce dernier ne pousse la largeur/hauteur live que
// via des props React (jamais dans le store) tant qu'on n'a pas relâché, donc
// box.width/height reflètent déjà la taille en cours mais box.content.shapes
// non — le rapport entre les deux donne l'échelle à appliquer visuellement
// (un <Group scaleX/scaleY>, cf. getShapeComponent) sans recalculer chaque
// sous-forme à chaque frame.
//
// `resizeStartWidth`/`resizeStartHeight` : props transitoires (Resizer.jsx ->
// Box.jsx -> ici), posées uniquement pendant un glissement en cours — jamais
// persistées, donc rien à migrer pour les formes composées déjà existantes,
// et aucun risque de valeur périmée après un changement de taille par une
// autre voie (champ Largeur du panneau, modification distante...) puisque la
// prop n'existe tout simplement pas hors resize actif.
export const getCompoundDesignScale = (box) => {
  if (!isCompoundShape(box)) return { scaleX: 1, scaleY: 1 };
  const designWidth = box.resizeStartWidth ?? box.width;
  const designHeight = box.resizeStartHeight ?? box.height;
  return {
    scaleX: designWidth ? box.width / designWidth : 1,
    scaleY: designHeight ? box.height / designHeight : 1,
  };
};

// Distance (px, à l'échelle du document — pas du zoom écran) en dessous de
// laquelle un bord/centre de la sous-forme déplacée accroche un repère.
export const SNAP_THRESHOLD = 4;

const edgePoints = (x, y, width, height) => ({
  xs: [x, x + width / 2, x + width],
  ys: [y, y + height / 2, y + height],
});

// Calcule l'accroche d'une sous-forme en cours de déplacement : compare ses
// bords/centre (gauche, centre, droite — haut, milieu, bas) à ceux du canevas
// et de chaque autre sous-forme, et renvoie la position corrigée dès que l'un
// d'eux tombe sous SNAP_THRESHOLD. `guideX`/`guideY` (coordonnées du repère
// accroché, dans le référentiel de la box) pilotent l'affichage du filet de
// guidage — null si rien n'accroche sur cet axe. `guideXSource`/`guideYSource`
// disent d'où vient le repère ('canvas' ou 'shape') pour en choisir la couleur ;
// à distance égale, le canevas l'emporte (il est listé en premier).
export const computeSnap = (box, subShapeId, x, y) => {
  const sub = box.content.shapes.find((s) => s.id === subShapeId);
  if (!sub) return { x, y, guideX: null, guideY: null, guideXSource: null, guideYSource: null };
  const moving = edgePoints(x, y, sub.width, sub.height);

  const candidatesX = [0, box.width / 2, box.width].map((value) => ({ value, source: 'canvas' }));
  const candidatesY = [0, box.height / 2, box.height].map((value) => ({ value, source: 'canvas' }));
  box.content.shapes.forEach((other) => {
    if (other.id === subShapeId) return;
    const o = edgePoints(other.x ?? 0, other.y ?? 0, other.width, other.height);
    candidatesX.push(...o.xs.map((value) => ({ value, source: 'shape' })));
    candidatesY.push(...o.ys.map((value) => ({ value, source: 'shape' })));
  });

  const snapAxis = (position, movingPoints, candidates) => {
    let snapped = position;
    let guide = null;
    let guideSource = null;
    let bestDelta = SNAP_THRESHOLD;
    movingPoints.forEach((point) => {
      candidates.forEach(({ value, source }) => {
        const delta = value - point;
        if (Math.abs(delta) < bestDelta) {
          bestDelta = Math.abs(delta);
          snapped = position + delta;
          guide = value;
          guideSource = source;
        }
      });
    });
    return { snapped, guide, guideSource };
  };

  const snapX = snapAxis(x, moving.xs, candidatesX);
  const snapY = snapAxis(y, moving.ys, candidatesY);
  return {
    x: snapX.snapped,
    y: snapY.snapped,
    guideX: snapX.guide,
    guideY: snapY.guide,
    guideXSource: snapX.guideSource,
    guideYSource: snapY.guideSource,
  };
};

// Un lien est un seul Konva.Shape dessiné à la main (trait + marqueurs) : son
// tracé vit dans l'attribut `geometry`, que l'éditeur plein écran peut
// remplacer directement pendant un geste (cf. refreshLinksLive) sans repasser
// par React. `fromId`/`toId`/`linkType`/`fromAnchor`/`toAnchor` lui disent quoi
// recalculer.
const sceneLink = (ctx, shape) => {
  const geometry = shape.getAttr('geometry');
  if (!geometry) return;
  const style = shape.getAttr('linkStyle');
  traceLinkPath(ctx, geometry, style);
  ctx.strokeShape(shape);
  if (!shape.getAttr('withMarkers')) return;
  const sizes = markerSizes(style);
  const { points } = geometry;
  [
    [style.startMarker, points[0], geometry.startAngle, sizes.start],
    [style.endMarker, points[points.length - 1], geometry.endAngle, sizes.end],
  ].forEach(([marker, tip, angle, size]) => {
    if (!traceMarker(ctx, marker, tip, angle, size)) return;
    ctx.setAttr('fillStyle', style.color);
    ctx.fill();
  });
};

// Zone cliquable : le trait seul, élargi par hitStrokeWidth.
const hitLink = (ctx, shape) => {
  const geometry = shape.getAttr('geometry');
  if (!geometry) return;
  traceLinkPath(ctx, geometry, shape.getAttr('linkStyle'));
  ctx.strokeShape(shape);
};

const renderLink = ({ ownerId, link, from, to }, { isSelected, onSelectLink, onEditLinkText }) => {
  const common = {
    geometry: computeLinkGeometry(link.type, from, to, link),
    linkId: link.id,
    fromId: from.id,
    toId: to.id,
    fromAnchor: link.fromAnchor,
    toAnchor: link.toAnchor,
    linkType: link.type,
    sceneFunc: sceneLink,
    lineCap: 'round',
    lineJoin: 'round',
    perfectDrawEnabled: false,
  };
  const select = onSelectLink
    ? (e) => {
      e.cancelBubble = true;
      onSelectLink(ownerId, link.id);
    }
    : undefined;
  return (
    <Group key={link.id}>
      {isSelected && (
        <KonvaShape
          {...common}
          name='shapeLink subShapeSelectionIndicator'
          linkStyle={{ ...link, startMarker: 'none', endMarker: 'none' }}
          withMarkers={false}
          stroke={SUB_SHAPE_COLOR}
          strokeWidth={link.strokeWidth + 6}
          opacity={0.35}
          listening={false}
        />
      )}
      <KonvaShape
        {...common}
        name='shapeLink'
        linkStyle={link}
        withMarkers
        stroke={link.color}
        strokeWidth={link.strokeWidth}
        dash={link.dash ? [link.strokeWidth * 3, link.strokeWidth * 2] : undefined}
        listening={!!onSelectLink}
        hitFunc={hitLink}
        hitStrokeWidth={Math.max(12, link.strokeWidth + 8)}
        onClick={select}
        onTap={select}
        onDblClick={onEditLinkText ? () => onEditLinkText(ownerId, link.id) : undefined}
        onDblTap={onEditLinkText ? () => onEditLinkText(ownerId, link.id) : undefined}
      />
    </Group>
  );
};

// `margin` : espace (px) réservé autour de la forme sur le Stage agrandi (cf.
// getShadowCanvasMargin + Shape.jsx, qui dimensionne le Stage en conséquence).
// Une simple translation Konva plutôt qu'un recalcul des coordonnées propres à
// chaque forme (Rect part d'un coin, les autres de leur centre).
//
// `interaction` (optionnel) : sélection/déplacement des sous-formes d'une
// forme composée — absent (rendu PDF, aperçu...), aucune interactivité n'est
// montée sur le Stage.
export const getShapeComponent = (box, landscape, margin = 0, interaction = {}) => {
  if (isCompoundShape(box)) {
    const {
      selectedSubShapeId,
      selectedSubShapeIds,
      onSelectSubShape,
      onDragSubShapeEnd,
      onDragSubShapeMove,
      onDragSubShapesEnd,
      onTransformSubShapeEnd,
      registerSubShapeNode,
      selectedLinkId,
      onSelectLink,
      onSubShapeLiveChange,
      onEditSubShapeText,
      onEditLinkText,
    } = interaction;
    // Sélection multiple (`selectedSubShapeIds`, plusieurs formes) : elles se
    // déplacent ensemble (le Transformer de Konva entraîne les autres nœuds
    // attachés), sans accroche ni redimensionnement (cf. l'éditeur).
    const isSubShapeSelected = (id) => (selectedSubShapeIds ? selectedSubShapeIds.includes(id) : selectedSubShapeId === id);
    const isMultiSelection = (selectedSubShapeIds?.length ?? 0) > 1;
    // Aperçu en direct de la box en cours de redimensionnement (cf.
    // getCompoundDesignScale) : un Group intermédiaire à cette échelle, lui-
    // même dans un Group non mis à l'échelle qui porte `margin` — sans quoi
    // la marge (ombre) grandirait ou rétrécirait avec le contenu au lieu de
    // rester un cadre constant autour du Stage.
    const { scaleX, scaleY } = getCompoundDesignScale(box);
    return (
      // Rogné aux bords de la box : une sous-forme peut être déplacée/
      // redimensionnée au-delà (utile pour composer, ex. ne montrer qu'un
      // quart d'un cercle), mais ce qui dépasse ne doit pas rester visible —
      // comme un cadre / calque avec « rogner le contenu » actif ailleurs.
      <Group name='compoundClipGroup' x={margin} y={margin} clipX={0} clipY={0} clipWidth={box.width} clipHeight={box.height}>
        <Group scaleX={scaleX} scaleY={scaleY}>
          {box.content.shapes.map((sub) => (
            <Group
              key={sub.id}
              ref={registerSubShapeNode ? (node) => registerSubShapeNode(sub.id, node) : undefined}
              width={sub.width}
              height={sub.height}
              // sub.x/y = coin haut-gauche de la sous-forme non tournée ; le
              // pivot (offset) est son centre — intersection des diagonales de
              // la box, comme pour une forme simple — d'où la position du
              // Group décalée d'une demi-taille.
              x={(sub.x ?? 0) + sub.width / 2}
              y={(sub.y ?? 0) + sub.height / 2}
              offsetX={sub.width / 2}
              offsetY={sub.height / 2}
              rotation={sub.rotation ?? 0}
              draggable={!!onDragSubShapeEnd}
              onClick={onSelectSubShape ? (e) => onSelectSubShape(sub.id, e.evt) : undefined}
              onTap={onSelectSubShape ? (e) => onSelectSubShape(sub.id, e.evt) : undefined}
              onDblClick={onEditSubShapeText ? () => onEditSubShapeText(sub.id) : undefined}
              onDblTap={onEditSubShapeText ? () => onEditSubShapeText(sub.id) : undefined}
              onDragMove={
                onDragSubShapeMove
                  ? (e) => {
                    const node = e.target;
                    // Les liens suivent aussi un déplacement de groupe :
                    // l'éditeur relit la position de tous les nœuds.
                    onSubShapeLiveChange?.();
                    if (isMultiSelection && isSubShapeSelected(sub.id)) return;
                    const snapped = computeSnap(box, sub.id, node.x() - sub.width / 2, node.y() - sub.height / 2);
                    // Applique l'accroche directement au nœud pendant le
                    // glissement : le rendu React ne repart de sub.x/y qu'au
                    // dépôt (onDragEnd), sans ça la forme ne suivrait le
                    // repère qu'une fois relâchée.
                    node.x(snapped.x + sub.width / 2);
                    node.y(snapped.y + sub.height / 2);
                    onDragSubShapeMove({
                      guideX: snapped.guideX,
                      guideY: snapped.guideY,
                      guideXSource: snapped.guideXSource,
                      guideYSource: snapped.guideYSource,
                    });
                  }
                  : undefined
              }
              onDragEnd={
                onDragSubShapeEnd
                  ? (e) => {
                    if (isMultiSelection && isSubShapeSelected(sub.id)) {
                      onDragSubShapesEnd?.();
                      return;
                    }
                    onDragSubShapeEnd(sub.id, { x: e.target.x() - sub.width / 2, y: e.target.y() - sub.height / 2 });
                    onDragSubShapeMove?.({ guideX: null, guideY: null, guideXSource: null, guideYSource: null });
                  }
                  : undefined
              }
              onTransform={onSubShapeLiveChange ? () => onSubShapeLiveChange() : undefined}
              onTransformEnd={
                onTransformSubShapeEnd
                  ? (e) => {
                    // Le Transformer Konva change scaleX/scaleY, pas
                    // width/height : on les convertit puis on remet l'échelle
                    // à 1, sans quoi le prochain rendu (qui repart de
                    // sub.width/height) doublerait l'effet à la prochaine
                    // transformation.
                    const node = e.target;
                    const nodeScaleX = node.scaleX();
                    const nodeScaleY = node.scaleY();
                    const width = Math.max(10, Math.round(sub.width * nodeScaleX));
                    const height = Math.max(10, Math.round(sub.height * nodeScaleY));
                    node.scaleX(1);
                    node.scaleY(1);
                    // Le pivot étant le centre, node.x/y reste le centre de la
                    // sous-forme après redimensionnement : on en déduit le coin
                    // haut-gauche non tourné avec les nouvelles dimensions.
                    onTransformSubShapeEnd(sub.id, {
                      x: node.x() - width / 2,
                      y: node.y() - height / 2,
                      width,
                      height,
                      rotation: node.rotation(),
                    });
                  }
                  : undefined
              }
            >
              {getShapeElement(subShapeDescriptor(sub))}
              {/* Sans fond ni bordure, un bloc texte serait introuvable dans
                  l'éditeur : un cadre pointillé discret, propre à l'édition. */}
              {sub.shape === 'text' && onSelectSubShape && (
                <Rect
                  name='linkEditorUi'
                  width={sub.width}
                  height={sub.height}
                  stroke='#bfbfbf'
                  strokeWidth={1}
                  dash={[3, 3]}
                  listening={false}
                />
              )}
              {isSubShapeSelected(sub.id) && (
                // Exactement sub.width/height (comme le cadre du Transformer,
                // cf. registerSubShapeNode) : le Stage déborde désormais de la
                // box (cf. ShapeFullscreenEditor), plus besoin d'un retrait
                // pour qu'il reste visible sur une sous-forme bord à bord.
                <Rect
                  name='subShapeSelectionIndicator'
                  x={0}
                  y={0}
                  width={sub.width}
                  height={sub.height}
                  stroke={SUB_SHAPE_COLOR}
                  strokeWidth={1}
                  dash={[4, 4]}
                  listening={false}
                />
              )}
            </Group>
          ))}
          {/* Par-dessus les formes : un marqueur centré sur le bord (point,
              carré) y serait sinon à moitié caché. */}
          {collectLinks(box.content.shapes).map((entry) => renderLink(entry, {
            isSelected: entry.link.id === selectedLinkId,
            onSelectLink,
            onEditLinkText,
          }))}
        </Group>
      </Group>
    );
  }

  const shapeElement = getShapeElement(legacyDescriptor(box));
  if (margin <= 0) return shapeElement;
  return (
    <Group x={margin} y={margin}>
      {shapeElement}
    </Group>
  );
};

const defaultSubShapeShadow = () => ({
  enabled: false,
  color: { ...defaultItemValues.shape.shadow.color },
  blur: defaultItemValues.shape.shadow.blur,
  offsetX: defaultItemValues.shape.shadow.offsetX,
  offsetY: defaultItemValues.shape.shadow.offsetY,
  opacity: defaultItemValues.shape.shadow.opacity,
});

// Construit une sous-forme neuve, mêmes champs qu'une box « Forme » classique
// mais à plat dans le tableau (pas de niveau `content` imbriqué : une sous-
// forme n'a pas besoin de porter un `type` de box).
export const createSubShape = ({ id, shape, x, y, width, height, color, svgPath, svgViewBox, svgDataUrl }) => ({
  id,
  shape,
  color,
  x,
  y,
  width,
  height,
  rotation: 0,
  radius: 0,
  borders: {
    color: { ...defaultItemValues.shape.borders.color },
    size: defaultItemValues.shape.borders.size,
  },
  shadow: defaultSubShapeShadow(),
  ...(shape === 'arrow' ? { arrows: { ...defaultItemValues.shape.arrows }, stroke: defaultItemValues.shape.stroke } : {}),
  ...(shape === 'svg' ? { svgPath, svgViewBox } : {}),
  ...(shape === 'svg-image' ? { svgDataUrl, svgViewBox } : {}),
});

// Bascule une box « Forme » simple en forme composée : la forme actuelle
// devient la première sous-forme (mêmes réglages, position 0,0), et une
// deuxième sous-forme par défaut est ajoutée en léger décalage. `newIds`
// fournit les ids Konva des deux sous-formes (générés par l'appelant, qui a
// besoin de connaître celui de la nouvelle sous-forme pour la sélectionner).
// `defaultColor` : couleur par défaut des formes du document (configuration.
// shapeColor.style, cf. initializeBoxContent) — la même que reçoit une forme
// simple neuve, pas une couleur propre aux formes composées.
// La forme simple d'origine, telle qu'elle devient la première sous-forme —
// extrait de mutateStartCompoundShape pour être réutilisé par sa variante
// import (cf. mutateStartCompoundShapeFromImport), seule la deuxième
// sous-forme diffère.
const boxAsFirstSubShape = (box, firstId) => ({
  id: firstId,
  shape: box.content.shape,
  color: box.content.color,
  x: 0,
  y: 0,
  width: box.width,
  height: box.height,
  rotation: box.rotation ?? 0,
  radius: box.radius ?? 0,
  cornerRadii: box.cornerRadii,
  borders: box.borders,
  shadow: box.shadow,
  ...(box.content.shape === 'arrow' ? { arrows: box.arrows, stroke: box.stroke } : {}),
});

// Bascule une box « Forme » simple en forme composée à une seule sous-forme
// (l'originale, sans ajout d'une deuxième par défaut) — pour l'iframe
// "composer une forme" de la banque admin (cf. shapeScratchApp.jsx), qui a
// besoin d'une box déjà compound dès sa création : ShapeFullscreenEditor,
// jusqu'ici toujours ouvert depuis une box déjà compound (cf. le bouton
// plein écran, visible seulement dans ce cas), suppose isCompound sans le
// revérifier.
export const mutateMakeCompoundWithSingleSubShape = (box, firstId) => {
  box.content.shapes = [boxAsFirstSubShape(box, firstId)];
};

// Inverse de mutateMakeCompoundWithSingleSubShape : si la seule sous-forme
// `subShapeId` est encore exactement celle que la forme simple d'origine aurait
// donnée (rien n'a été modifié dans l'éditeur plein écran), on repasse en forme
// simple. Ouvrir l'éditeur puis le refermer sans rien faire ne doit pas
// transformer une forme simple en forme « Personnalisé ».
export const mutateCollapseUnchangedSubShape = (box, subShapeId) => {
  const shapes = box.content?.shapes ?? [];
  if (shapes.length !== 1 || shapes[0].id !== subShapeId) return;
  const withoutUndefined = (sub) => _.omitBy(sub, _.isUndefined);
  if (!_.isEqual(withoutUndefined(shapes[0]), withoutUndefined(boxAsFirstSubShape(box, subShapeId)))) return;
  delete box.content.shapes;
};

export const mutateStartCompoundShape = (box, [firstId, secondId], defaultColor) => {
  const offset = Math.round(Math.min(box.width, box.height) * 0.2);
  const secondSubShape = createSubShape({
    id: secondId,
    shape: 'square',
    x: offset,
    y: offset,
    width: Math.max(20, Math.round(box.width * 0.6)),
    height: Math.max(20, Math.round(box.height * 0.6)),
    color: defaultColor,
  });
  box.content.shapes = [boxAsFirstSubShape(box, firstId), secondSubShape];
};

// Équivalent de mutateStartCompoundShape quand la toute première sous-forme
// ajoutée est un import SVG (cf. ShapeComponent#handleSvgImportChoice) : la
// forme simple d'origine devient pareillement la première sous-forme, seule
// la deuxième (l'import) diffère — dimensionnée selon le ratio de son viewBox
// plutôt qu'en carré. `shape` : 'svg' (recolorable) ou 'svg-image' (couleurs
// d'origine, cf. le choix proposé à l'utilisateur) — seuls svgPath (forme) ou
// svgDataUrl (image) sont fournis selon le cas, l'autre reste undefined.
export const mutateStartCompoundShapeFromImport = (box, [firstId, secondId], { shape, svgPath, svgViewBox, svgDataUrl, color }) => {
  const offset = Math.round(Math.min(box.width, box.height) * 0.2);
  const width = Math.max(20, Math.round(box.width * 0.6));
  const ratio = svgViewBox.width ? svgViewBox.height / svgViewBox.width : 1;
  const secondSubShape = createSubShape({
    id: secondId,
    shape,
    x: offset,
    y: offset,
    width,
    height: Math.round(width * ratio),
    color,
    svgPath,
    svgViewBox,
    svgDataUrl,
  });
  box.content.shapes = [boxAsFirstSubShape(box, firstId), secondSubShape];
};

// Ajoute une sous-forme à une forme déjà composée, en léger décalage par
// rapport à la dernière ajoutée pour qu'elle ne tombe pas exactement dessus.
export const mutateAddSubShape = (box, newId, defaultColor) => {
  const last = box.content.shapes[box.content.shapes.length - 1];
  const offset = 20;
  box.content.shapes.push(
    createSubShape({
      id: newId,
      shape: 'square',
      x: (last?.x ?? 0) + offset,
      y: (last?.y ?? 0) + offset,
      width: last?.width ?? Math.round(box.width * 0.4),
      height: last?.height ?? Math.round(box.height * 0.4),
      color: defaultColor,
    })
  );
};

// Équivalent de mutateAddSubShape pour un import SVG (cf.
// ShapeComponent#handleSvgImportChoice) : même décalage par rapport à la
// dernière sous-forme, dimensionné selon le ratio du viewBox plutôt qu'en
// carré. Même distinction 'svg'/'svg-image' que mutateStartCompoundShapeFromImport.
export const mutateAddImportedSubShape = (box, newId, { shape, svgPath, svgViewBox, svgDataUrl, color }) => {
  const last = box.content.shapes[box.content.shapes.length - 1];
  const offset = 20;
  const width = last?.width ?? Math.round(box.width * 0.4);
  const ratio = svgViewBox.width ? svgViewBox.height / svgViewBox.width : 1;
  box.content.shapes.push(
    createSubShape({
      id: newId,
      shape,
      x: (last?.x ?? 0) + offset,
      y: (last?.y ?? 0) + offset,
      width,
      height: Math.round(width * ratio),
      color,
      svgPath,
      svgViewBox,
      svgDataUrl,
    })
  );
};

// Remplace l'import d'une sous-forme SVG déjà en place (cf.
// ShapeComponent#handleReplaceSvg) : la position/taille/couleur/bordure...
// choisies par l'utilisateur restent, seul le dessin importé change — y
// compris son mode ('svg' <-> 'svg-image'), l'utilisateur pouvant changer
// d'avis au moment de remplacer.
export const mutateReplaceSubShapeImport = (subShape, { shape, svgPath, svgViewBox, svgDataUrl }) => {
  subShape.shape = shape;
  subShape.svgViewBox = svgViewBox;
  if (shape === 'svg') {
    subShape.svgPath = svgPath;
    delete subShape.svgDataUrl;
  } else {
    subShape.svgDataUrl = svgDataUrl;
    delete subShape.svgPath;
  }
};

export const mutateRemoveSubShape = (box, subShapeId) => {
  box.content.shapes = box.content.shapes.filter((s) => s.id !== subShapeId);
  mutateRemoveLinksTo(box, subShapeId);
};

// La copie part sans les liens de l'original : elle arrive par-dessus, ses liens
// doubleraient exactement les siens.
export const mutateDuplicateSubShape = (box, subShapeId, newId) => {
  const source = box.content.shapes.find((s) => s.id === subShapeId);
  if (!source) return;
  const { links, ...copy } = source;
  box.content.shapes.push({ ...copy, id: newId, x: source.x + 15, y: source.y + 15 });
};

// Ordre d'empilement des sous-formes : la dernière du tableau se dessine
// au-dessus (cf. getShapeComponent, qui les rend dans l'ordre du tableau).
// `direction` : -1 pour reculer d'un cran (vers le fond, index plus bas),
// +1 pour avancer d'un cran (vers le dessus, index plus haut).
export const mutateReorderSubShape = (box, subShapeId, direction) => {
  const shapes = box.content.shapes;
  const index = shapes.findIndex((s) => s.id === subShapeId);
  const targetIndex = index + direction;
  if (index === -1 || targetIndex < 0 || targetIndex >= shapes.length) return;
  [shapes[index], shapes[targetIndex]] = [shapes[targetIndex], shapes[index]];
};

// Équivalent de mutateSetShapeKind pour une sous-forme : pas de passage
// possible vers « ligne » (une sous-forme n'est pas un type de box), donc pas
// besoin de la branche forme<->ligne de mutateSetShapeKind.
export const mutateSetSubShapeKind = (subShape, shapeId) => {
  if (!subShape || subShape.shape === shapeId) return;
  if (shapeId === 'arrow') {
    subShape.arrows = subShape.arrows ?? { ...defaultItemValues.shape.arrows };
    subShape.stroke = subShape.stroke ?? defaultItemValues.shape.stroke;
    subShape.height = subShape.stroke + subShape.arrows.stroke + 20;
  } else if (LOCK_PROPORTIONS_SHAPES.includes(shapeId)) {
    subShape.height = subShape.width * shapeRatios(shapeId);
  }
  delete subShape.cornerRadii;
  subShape.shape = shapeId;
};
